import { WorkbenchPage } from "@/components/page-templates";
import { ReleaseStageBadge } from "@/components/release-stage-badge";
import { RequireScope } from "@/components/require-scope";
import {
  PageTabsList,
  PageTabsTrigger,
  Tabs,
  TabsContent,
} from "@/components/ui/Tabs";
import { useTelemetry } from "@/contexts/Telemetry";
import { BudgetsContent } from "@/pages/budgets/Budgets";
import { useMemo, useState, type JSX } from "react";
import { useLocation } from "react-router";
import { CostsExplorer } from "./CostsExplorer";
import { displayName, parseDrillPath } from "./taxonomy";

type CostsTab = "costs" | "budgets";

// Map each drill segment (`Dimension~encodedValue`) to its pretty label so the
// breadcrumb bar renders "R&D", "Olivia Novak", … instead of the raw encoding.
// Derived straight from the pathname (not the resolved route href) and keyed
// by the literal path segment, so the substitution is present and matches on
// every render — no slug-resolution race, no raw-segment flash.
function useCostsBreadcrumbSubstitutions(): Record<string, string> {
  const location = useLocation();

  return useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    const costsIndex = segments.indexOf("costs");
    const map: Record<string, string> = {};
    if (costsIndex >= 0) {
      for (const rawSegment of segments.slice(costsIndex + 1)) {
        const [crumb] = parseDrillPath(rawSegment);
        if (crumb) map[rawSegment] = displayName(crumb.dim, crumb.value);
      }
    }
    return map;
  }, [location.pathname]);
}

// New cost-taxonomy dashboard. Part of the Observe surface, gated on org:admin
// so basic members (synced via directory/SCIM) don't see it by default.
function NewCostsPage(): JSX.Element {
  const breadcrumbSubstitutions = useCostsBreadcrumbSubstitutions();

  return (
    <WorkbenchPage
      scope="org:admin"
      breadcrumbSubstitutions={breadcrumbSubstitutions}
    >
      {/* WorkbenchPage owns overflow-hidden; the explorer expects its container
          to scroll (matching the Costs tab in TabbedCostsPage). */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <CostsExplorer />
      </div>
    </WorkbenchPage>
  );
}

// The Costs page with a Budgets tab beside the cost explorer. Tab state is
// local (PolicyCenter pattern): the drill URL under /costs/... keeps working
// and always lands on the Costs tab. The tab strip lives inside the content
// column — aligned with each tab's max-width content, like PolicyCenter's
// Policies/Exclusions strip — not up in the page chrome beside the
// breadcrumbs.
function TabbedCostsPage(): JSX.Element {
  const [activeTab, setActiveTab] = useState<CostsTab>("costs");
  const breadcrumbSubstitutions = useCostsBreadcrumbSubstitutions();

  return (
    <WorkbenchPage breadcrumbSubstitutions={breadcrumbSubstitutions}>
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as CostsTab)}
        className="min-h-0 flex-1 gap-0"
      >
        {/* Full-bleed border so the hairline matches the full-width
            explorer header below; tabs stay aligned to the content column. */}
        <div className="border-border w-full border-b pt-6">
          <div className="mx-auto w-full max-w-7xl px-8">
            <PageTabsList>
              <PageTabsTrigger value="costs">Costs</PageTabsTrigger>
              <PageTabsTrigger
                value="budgets"
                className="inline-flex items-center gap-2"
              >
                Budgets
                <ReleaseStageBadge stage="preview" noTooltip />
              </PageTabsTrigger>
            </PageTabsList>
          </div>
        </div>
        <TabsContent value="costs" className="min-h-0 overflow-y-auto">
          <RequireScope scope="org:admin" level="page">
            <CostsExplorer />
          </RequireScope>
        </TabsContent>
        <TabsContent value="budgets" className="min-h-0 overflow-y-auto">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-4 p-8 pt-6 pb-24">
            <RequireScope scope="org:admin" level="page">
              <BudgetsContent />
            </RequireScope>
          </div>
        </TabsContent>
      </Tabs>
    </WorkbenchPage>
  );
}

// Budgets remains independently gated because the same flag also controls
// spend-rule enforcement on the server.
export default function Costs(): JSX.Element {
  const telemetry = useTelemetry();
  const budgetsEnabled = telemetry.isFeatureEnabled("gram-budgets") ?? false;

  return budgetsEnabled ? <TabbedCostsPage /> : <NewCostsPage />;
}
